import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Snowflake } from "lucide-react";

export default function ThermalSimulator() {
  const [mode, setMode] = useState("cool");
  const canvasRef = useRef(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const cool = mode === "cool";

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const parts = Array.from({ length: 100 }, () => ({
      a: Math.random() * Math.PI * 2,
      r: 44 + Math.random() * 150,
      s: 0.0015 + Math.random() * 0.005,
      sz: 0.9 + Math.random() * 1.7,
      o: 0.3 + Math.random() * 0.7,
    }));

    const draw = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      const cx = w / 2;
      const cy = h / 2;
      const isCool = modeRef.current === "cool";
      const dir = isCool ? -1 : 1;
      ctx.clearRect(0, 0, w, h);

      for (let i = 1; i <= 4; i++) {
        ctx.beginPath();
        ctx.arc(cx, cy, 48 * i, 0, Math.PI * 2);
        ctx.strokeStyle = isCool ? "rgba(56,189,248,0.09)" : "rgba(255,107,0,0.09)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      const pts = [];
      for (const p of parts) {
        p.a += p.s * dir;
        const wobble = Math.sin(p.a * 3 + p.r) * 7;
        const x = cx + Math.cos(p.a) * (p.r + wobble);
        const y = cy + Math.sin(p.a) * (p.r + wobble) * 0.9;
        pts.push([x, y, p.o]);
      }

      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i][0] - pts[j][0];
          const dy = pts[i][1] - pts[j][1];
          const d2 = dx * dx + dy * dy;
          if (d2 < 2500) {
            ctx.beginPath();
            ctx.moveTo(pts[i][0], pts[i][1]);
            ctx.lineTo(pts[j][0], pts[j][1]);
            ctx.strokeStyle = isCool
              ? `rgba(56,189,248,${0.09 * (1 - d2 / 2500)})`
              : `rgba(255,107,0,${0.09 * (1 - d2 / 2500)})`;
            ctx.stroke();
          }
        }
      }

      for (const [x, y, o] of pts) {
        ctx.beginPath();
        ctx.arc(x, y, 1.7, 0, Math.PI * 2);
        ctx.fillStyle = isCool ? `rgba(56,189,248,${o})` : `rgba(255,140,50,${o})`;
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section data-testid="thermal-section" className="relative overflow-hidden py-24 sm:py-32">
      <div
        className={`pointer-events-none absolute left-1/2 top-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[160px] transition-colors duration-1000 ${
          cool ? "bg-ice/10" : "bg-forge/10"
        }`}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mb-12 max-w-2xl text-center"
        >
          <span className={`font-mono text-[10px] uppercase tracking-[0.26em] ${cool ? "text-ice" : "text-forge"}`}>
            03 — Thermodynamics, Visualized
          </span>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Watch your air move.
          </h2>
          <p className="mt-4 text-base text-slate-400 sm:text-lg">
            This is how a Crowson-balanced system circulates conditioned air —
            laminar, continuous, and silent. Flip the force.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto max-w-3xl"
        >
          <div className="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-navy-900/50 backdrop-blur">
            <canvas ref={canvasRef} data-testid="thermal-sim-canvas" className="block h-[380px] w-full sm:h-[440px]" />
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <div
                className={`font-display text-6xl font-extrabold tracking-tight transition-colors duration-700 sm:text-7xl ${
                  cool ? "text-ice" : "text-forge"
                }`}
              >
                {cool ? "68" : "74"}
                <span className="text-3xl align-top">°F</span>
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
                {cool ? "Ice-calibrated setpoint" : "Forge-grade setpoint"}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-center gap-3">
            <button
              data-testid="thermal-mode-cool"
              onClick={() => setMode("cool")}
              className={`flex items-center gap-2.5 rounded-full px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider transition-all duration-300 ${
                cool
                  ? "bg-gradient-to-br from-ice-lo to-ice text-navy-950 shadow-[0_8px_30px_rgba(56,189,248,0.35)]"
                  : "border border-white/10 bg-white/5 text-slate-400 hover:text-ice"
              }`}
            >
              <Snowflake className="h-4 w-4" />
              Cooling Flow
            </button>
            <button
              data-testid="thermal-mode-heat"
              onClick={() => setMode("heat")}
              className={`flex items-center gap-2.5 rounded-full px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider transition-all duration-300 ${
                !cool
                  ? "bg-gradient-to-br from-forge to-forge-hi text-white shadow-[0_8px_30px_rgba(255,107,0,0.35)]"
                  : "border border-white/10 bg-white/5 text-slate-400 hover:text-forge"
              }`}
            >
              <Flame className="h-4 w-4" />
              Heating Flow
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
